import { z } from 'zod';

/**
 * "Carbon tester" DTOs — the ops-portal page that lets an operator type in a
 * HYPOTHETICAL hardware configuration (not tied to any real asset/SKU row)
 * plus mock telemetry and get back embodied + Scope 2 use-phase carbon
 * figures computed by the SAME BoaviztAPI conversion logic the live
 * embodied/use-phase pipelines use (carbon-methodology/derive-input.ts,
 * boavizta-client.ts), reached via a new live route,
 * POST/GET /api/ops/carbon-tester.
 *
 * Backs db/schema/820_carbon_tester_run.sql — one row per submission,
 * auto-saved on every compute for the page's history list. Scratch/test
 * data, not a compliance record (unlike sku_carbon_footprint / asset_carbon_
 * footprint) — no append-only guarantee, no min/max/bounds contract beyond
 * plain numeric ordering.
 *
 * Embodied and use-phase are two INDEPENDENT BoaviztAPI calls (embodied: no
 * `duration` param, whole lifecycle; use-phase: `duration=8760`, annual) —
 * either leg can independently be 'computed' or 'unsupported', so their
 * status/value/error fields are fully separate, never a shared status.
 */

export const carbonTesterCreatedByRoleSchema = z.enum(['ops_readonly', 'ops_admin']);
export type CarbonTesterCreatedByRole = z.infer<typeof carbonTesterCreatedByRoleSchema>;

export const carbonTesterLegStatusSchema = z.enum(['computed', 'unsupported']);
export type CarbonTesterLegStatus = z.infer<typeof carbonTesterLegStatusSchema>;

/** Mirrors ResolvedPower['basis'] in carbon-methodology's use-phase compute. */
export const carbonTesterPowerBasisSchema = z.enum(['telemetry_measured', 'archetype_default']);
export type CarbonTesterPowerBasis = z.infer<typeof carbonTesterPowerBasisSchema>;

/**
 * POST /api/ops/carbon-tester request body. `inputSpecs` is class-specific
 * free-form jsonb (see the UI's per-class field maps) — the derive layer
 * validates/dispatches on it, not this schema; an asset class outside the 5
 * BoaviztAPI knows about (server/gpu/cpu/memory/rack_system) is legal here
 * and simply comes back with embodiedStatus: 'unsupported' (same as the live
 * pipeline for an asset_class with no embodied methodology). `usageLocation
 * Country` defaults to BoaviztAPI's own worldwide-average code ('WOR') when
 * omitted.
 */
export const carbonTesterCreateRequestSchema = z.object({
  assetClassId: z.string().min(1),
  inputSpecs: z.record(z.string(), z.unknown()).default({}),
  avgPowerDrawWatts: z.coerce.number().positive(),
  useTimeRatio: z.coerce.number().min(0).max(1),
  usageLocationCountry: z.string().length(3).default('WOR'),
});
export type CarbonTesterCreateRequest = z.infer<typeof carbonTesterCreateRequestSchema>;

/** One carbon_tester_run row, camelCased. Deliberately omits the raw Boavizta
 * response blobs (rawEmbodiedResponse/rawUsePhaseResponse) — those are
 * persisted for audit but are not needed by the UI and can be large. */
export const carbonTesterRunSchema = z.object({
  id: z.string().uuid(),
  createdByRole: carbonTesterCreatedByRoleSchema,
  assetClassId: z.string(),
  inputSpecs: z.record(z.string(), z.unknown()),
  avgPowerDrawWatts: z.coerce.number(),
  useTimeRatio: z.coerce.number(),
  usageLocationCountry: z.string().nullable(),
  methodologyVersion: z.string().nullable(),

  embodiedStatus: carbonTesterLegStatusSchema,
  embodiedGwpMin: z.coerce.number().nullable(),
  embodiedGwpKgco2eq: z.coerce.number().nullable(),
  embodiedGwpMax: z.coerce.number().nullable(),
  embodiedError: z.string().nullable(),

  usePhaseStatus: carbonTesterLegStatusSchema,
  usePhaseGwpAnnualMin: z.coerce.number().nullable(),
  usePhaseGwpAnnualKgco2eq: z.coerce.number().nullable(),
  usePhaseGwpAnnualMax: z.coerce.number().nullable(),
  resolvedWatts: z.coerce.number().nullable(),
  powerBasis: carbonTesterPowerBasisSchema.nullable(),
  gridIntensityGPerKwh: z.coerce.number().nullable(),
  usePhaseError: z.string().nullable(),

  createdAt: z.string(),
});
export type CarbonTesterRun = z.infer<typeof carbonTesterRunSchema>;

export const carbonTesterRunResponseSchema = z.object({ run: carbonTesterRunSchema });
export type CarbonTesterRunResponse = z.infer<typeof carbonTesterRunResponseSchema>;

/** GET /api/ops/carbon-tester — history list, newest first. */
export const carbonTesterHistoryResponseSchema = z.object({
  runs: z.array(carbonTesterRunSchema),
});
export type CarbonTesterHistoryResponse = z.infer<typeof carbonTesterHistoryResponseSchema>;
